"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getMyEffectivePermissions } from "@/lib/api/endpoints/permissions";
import type {
  EffectiveModulePermissionDto,
  PermissionAction,
} from "@/lib/api/types/enterprise";

import { useAuthStore } from "./store";
import type { AuthUser } from "./types";

export type ModulePermissionMap = Record<string, EffectiveModulePermissionDto>;

function actionFlagName(action: PermissionAction): keyof EffectiveModulePermissionDto {
  switch (action) {
    case "view":
      return "can_view";
    case "create":
      return "can_create";
    case "edit":
      return "can_edit";
    case "delete":
      return "can_delete";
    case "export":
      return "can_export";
    case "manage_permissions":
      return "can_manage_permissions";
  }
}

export function hasModulePermission(
  user: AuthUser | null,
  map: ModulePermissionMap | null | undefined,
  moduleKey: string,
  action: PermissionAction = "view",
): boolean {
  if (user?.role === "super_admin") {
    return true;
  }
  if (!user?.role) {
    return false;
  }
  const row = map?.[moduleKey];
  if (!row) {
    return false;
  }
  return Boolean(row[actionFlagName(action)]);
}

export function useEffectivePermissions() {
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const query = useQuery({
    queryKey: ["effective-permissions", user?.id ?? "anon", tenantId ?? "none"],
    queryFn: getMyEffectivePermissions,
    enabled: Boolean(user?.id),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const permissionMap = useMemo<ModulePermissionMap>(() => {
    const modules = query.data?.modules ?? [];
    return modules.reduce<ModulePermissionMap>((acc, module) => {
      acc[module.module_key] = module;
      return acc;
    }, {});
  }, [query.data?.modules]);

  return {
    ...query,
    permissionMap,
  };
}

export function useModulePermission(moduleKey: string, action: PermissionAction = "view") {
  const user = useAuthStore((s) => s.user);
  const permissions = useEffectivePermissions();
  const allowed = hasModulePermission(user, permissions.permissionMap, moduleKey, action);
  return {
    ...permissions,
    allowed,
  };
}
